import { observer } from 'mobx-react-lite'
import { type ReactElement, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { preferredView, rememberView } from 'app/components/action-center/boardView'
import { FocusModeComponent } from 'app/components/action-center/FocusModeComponent'
import { bandRuleColumn } from 'lib/filterRuleColumns'
import type { TreatmentBandId } from 'app/components/action-center/treatmentBands'
import { TreatmentBoardComponent } from 'app/components/action-center/TreatmentBoardComponent'
import { useFocusQueue } from 'app/components/action-center/useFocusQueue'
import { withKeptParams } from 'app/components/lists/LegacyListRoute'
import { FilterListView } from 'app/components/lists/filter/FilterListView'
import { ListBandPicker } from 'app/components/lists/ListBandPicker'
import { ListKindLine } from 'app/components/lists/ListKindLine'
import { ListSettingsButton } from 'app/components/lists/ListSettingsButton'
import { ListViewToggle } from 'app/components/lists/ListViewToggle'
import { useOpenListSettings } from 'app/components/lists/useOpenListSettings'
import { ManualListView } from 'app/components/lists/manual/ManualListView'
import { SystemListTable } from 'app/components/lists/SystemListTable'
import { useRememberedListView } from 'app/components/lists/useRememberedListView'
import { SystemListBoard } from 'app/components/lists/system/SystemListBoard'
import { Button } from 'app/components/ui/button'
import { useDocumentTitle } from 'app/hooks/useDocumentTitle'
import { useSidebarCounts } from 'app/hooks/useSidebarCounts'
import { useListStore } from 'app/stores/StoreContext'
import type { SystemListDef } from 'app/types/navigation'
import { useHandleSearchParams, useUser } from 'app/utils'
import { bg } from 'lib/background'
import { legacyListRedirect } from 'lib/legacyListRedirect'
import { type ListDef, listName, resolveList } from 'lib/lists'
import { ALL_BANDS, resolveListBand, resolveListView, SYSTEM_LIST_BY_SLUG } from 'lib/systemLists'

const FocusList = observer(function FocusList({
  queueId,
  viewToggle,
  bandPicker,
}: {
  queueId: string
  viewToggle: ReactElement
  bandPicker?: ReactNode
}): ReactElement {
  const props = useFocusQueue(queueId)
  return <FocusModeComponent {...props} viewToggle={viewToggle} bandPicker={bandPicker} embedded />
})

const ManualList = observer(function ManualList({
  list,
  onStagesChanged,
}: {
  list: Extract<ListDef, { source: `manual` }>
  onStagesChanged: () => void
}): ReactElement {
  useDocumentTitle(listName(list))
  return <ManualListView list={list.list} onStagesChanged={onStagesChanged} />
})

const SystemList = observer(function SystemList({ def }: { def: SystemListDef }): ReactElement {
  const user = useUser()
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [savedView, saveView, settingLoaded] = useRememberedListView(def.slug, def.views)
  // The sidebar row's number, so the board's title chip and the row agree.
  const { counts } = useSidebarCounts()
  const totalItems = counts.contacts?.[def.slug]
  // A system list's stage visibility is a per-person setting the board reads live, so there is nothing to refetch.
  const openSettings = useOpenListSettings({ source: `system`, def }, () => {})
  useDocumentTitle(def.name)

  // The legacy localStorage preference seeds the setting once, so nobody re-picks after the move.
  const legacyView = preferredView(user?.id, def.queue.id, def.queue.defaultView ?? `focus`)
  const view = resolveListView(def, searchParams.get(`view`), savedView, legacyView)
  // Board and table narrow by band as a Filters rule, opening where `resolveListBand` does
  // when the URL names none. One at a time has no Filters panel, so it keeps the picker.
  const bandColumn = useMemo(
    () =>
      def.bands &&
      bandRuleColumn({
        name: def.bandRuleName ?? def.name,
        // Contact columns all carry a group, so an ungrouped band rule would sit alone under "Other".
        group: def.recordType === `contacts` ? `Dates` : undefined,
        bands: def.bands,
        dayWindows: def.bandDayWindows,
        defaultBand: view === `board` || def.opensOnDefaultBand ? (def.defaultBand ?? def.bands[0]?.id) : undefined,
      }),
    [def, view],
  )

  // Without a `?view=` to render immediately, wait for the saved view to load so the
  // page never flashes its default view before swapping to the person's real one.
  if (!searchParams.has(`view`) && !settingLoaded) return <></>

  const viewToggle = (
    <ListViewToggle
      views={def.views}
      current={view}
      onSelect={(next) => {
        saveView(next)
        // `table` is the treatment board's own third view, remembered under the pipeline id
        // (see `boardView.ts`); a system list's queue id must never take it as an answer.
        if (next !== `table`) rememberView(user?.id, def.queue.id, next)
        if (next !== view) handleSearchParams({ view: next })
      }}
    />
  )

  const band = resolveListBand(def, searchParams.get(`band`), view)
  const bandPicker = <ListBandPicker def={def} band={band} view={view} />
  const settings = <ListSettingsButton onClick={openSettings} />

  if (view === `focus`)
    return (
      <FocusList
        queueId={(band?.queue ?? def.queue).id}
        viewToggle={viewToggle}
        bandPicker={
          <>
            {bandPicker}
            {settings}
          </>
        }
      />
    )
  if (def.queue.bundle === `treatment`) {
    return (
      <TreatmentBoardComponent
        view={view === `table` ? `table` : `board`}
        title={def.name}
        totalItems={totalItems}
        band={(band?.id as TreatmentBandId | undefined) ?? ALL_BANDS}
        headerControls={viewToggle}
        titleActions={settings}
        subheader={<ListKindLine kind="builtIn" />}
        bandColumn={bandColumn}
        fixedRules={{ source: def.name, rules: def.fixedRules({ needsActionDateEnabled: false }) }}
        hideExits
      />
    )
  }
  if (view === `table`)
    return (
      <SystemListTable def={def} band={band} viewToggle={viewToggle} titleActions={settings} bandColumn={bandColumn} />
    )
  return (
    <SystemListBoard
      def={def}
      headerControls={viewToggle}
      titleActions={settings}
      bandColumn={bandColumn}
      totalItems={totalItems}
    />
  )
})

export const ListPage = observer(function ListPage(): ReactElement {
  const { slug = `` } = useParams<{ slug: string }>()
  const location = useLocation()
  const listStore = useListStore()
  const list = resolveList(slug, listStore.hits)
  const retired = legacyListRedirect(location.pathname, location.search)
  // A pipeline write elsewhere (create, rename, delete) never touches the List store, and the
  // store's own read is cache-first, so a slug the loaded store does not know may only be news
  // to it. It is asked once per slug, from the network, before the slug is given up on.
  const refetchRequested = useRef<string | undefined>(undefined)
  const [refetchedSlug, setRefetchedSlug] = useState<string>()
  const unresolved = !list && listStore.list !== undefined && !retired
  useEffect(() => {
    if (!unresolved || refetchRequested.current === slug) return
    refetchRequested.current = slug
    bg(listStore.fetchList(undefined, { requestPolicy: `network-only` }).then(() => setRefetchedSlug(slug)))
  }, [unresolved, slug, listStore])

  useEffect(() => {
    // A system slug never resolves to a practice list, so it has no reason to ask the
    // list store for anything: without this a system list page's own mount would fire
    // a query it never reads, and a failure there would latch `listError` onto the store
    // for every OTHER list page to trip over next.
    // A failed read leaves `list` undefined with `listError` set: without that last guard
    // the loading flag falling back to false would re-run this effect and ask again forever.
    if (
      !SYSTEM_LIST_BY_SLUG.has(slug) &&
      listStore.list === undefined &&
      !listStore.listLoading &&
      !listStore.listError
    )
      bg(() => listStore.fetchList())
  }, [slug, listStore, listStore.list, listStore.listLoading, listStore.listError])

  if (!list) {
    // A list is only knowable once the workspace's lists are in, so a cold load
    // of one waits rather than bouncing its own URL.
    if (listStore.list === undefined) {
      if (!listStore.listError) return <></>
      return (
        <div className="flex items-center gap-3 p-8">
          <p className="text-body-sm text-subtle">{`Couldn't load your lists.`}</p>
          <Button variant="secondary" size="sm" onClick={() => bg(() => listStore.fetchList())}>
            Try again
          </Button>
        </div>
      )
    }
    // A retired slice slug (e.g. `/lists/treatment-cold`) still means
    // something: the family it now bands. Anything else falls back to Action
    // Center rather than a dead page.
    if (retired) return <Navigate to={retired} replace />
    // The refetch above is still out: the answer is not in yet.
    if (refetchedSlug !== slug) return <></>
    return <Navigate to={withKeptParams(`/action-center`, location.search)} replace />
  }

  if (list.source === `system`) return <SystemList def={list.def} />
  const onStagesChanged = () => bg(() => listStore.fetchList(undefined, { requestPolicy: `network-only` }))
  // Keyed per list: moving between lists must not carry the previous one's view state.
  if (list.source === `filter`)
    return <FilterListView key={list.slug} list={list.list} onStagesChanged={onStagesChanged} />
  return <ManualList key={list.slug} list={list} onStagesChanged={onStagesChanged} />
})
